import { Center, Text } from '@chakra-ui/react';
import { MessageReceiptStatus } from '@halo/chat-sdk';
import { MessageStatus } from '@halo/chat-sdk';
import {
  MessagingPendingIcon,
  MessagingSentIcon,
  MessagingReceivedIcon,
  MessagingReadIcon,
  MessagingErrorIcon,
  MessagingStatusReadProcess,
} from '@halo/ui-chat-react';

export interface MessageStatusBaseProps {
  status: MessageStatus | undefined;
  hideMember?: boolean;
  processScale?: number;
  onClickError?: () => void;
  showErrorText?: boolean;
}

export function MessageStatusBase({
  status,
  onClickError,
  processScale,
  showErrorText,
}: MessageStatusBaseProps) {
  switch (status) {
    case MessageStatus.Queued:
    case MessageStatus.Encrypted:
      return (
        <Center fontSize="sm" style={{ animation: 'spin 3s linear infinite' }}>
          <MessagingPendingIcon />
        </Center>
      );
    case MessageStatus.Sent:
    case MessageStatus.Decrypted:
      return <MessagingSentIcon transform={`scale(${processScale})`} />;
    case MessageStatus.Summarized:
      return <MessagingReceivedIcon />;
    case MessageStatus.Fetched:
      return <MessagingReadIcon />;
    case MessageStatus.FailedSent:
    case MessageStatus.FailedEncrypt:
    case MessageStatus.FailedDecrypt:
      return (
        <MessagingErrorIcon
          cursor="pointer"
          transform={`scale(${processScale})`}
          onClick={onClickError}
        />
      );
    case MessageStatus.FailedSendFile:
      return (
        <>
          <MessagingErrorIcon cursor="default" transform={`scale(${processScale})`} />
          {showErrorText && (
            <Text fontSize="xs" color="fg.muted">
              Upload Failed
            </Text>
          )}
        </>
      );
    default:
      return null;
  }
}

export interface MessagingStatusSingleProps extends MessageStatusBaseProps {
  receiptStatus?: MessageReceiptStatus;
  showErrorText?: boolean;
}

export function MessageStatusSingle({
  status,
  receiptStatus,
  processScale = 1.1,
  ...props
}: MessagingStatusSingleProps) {
  switch (receiptStatus) {
    case MessageReceiptStatus.Delivered:
      return <MessagingReceivedIcon transform={`scale(${processScale})`} />;
    case MessageReceiptStatus.Read:
      return <MessagingReadIcon transform={`scale(${processScale})`} />;
    default:
      return <MessageStatusBase status={status} processScale={processScale} {...props} />;
  }
}

export interface MessagingStatusGroupProps extends MessageStatusBaseProps {
  readCount: number | undefined;
  totalCount: number | undefined;
  processScale?: number;
  renderProcess?: (node: React.ReactNode) => React.ReactNode;
  color?: string;
  hideMember?: boolean;
  showErrorText?: boolean;
}

export function MessageStatusGroup({
  status,
  readCount = 0,
  totalCount = 1,
  processScale = 1.2,
  renderProcess,
  color,
  hideMember,
  ...props
}: MessagingStatusGroupProps) {
  const process = useMemo(() => {
    return readCount / Math.max(totalCount, 1);
  }, [readCount, totalCount]);

  const processNode = useMemo(() => {
    const node = (
      <MessagingStatusReadProcess
        hideMember={hideMember}
        color={color}
        process={process}
        processScale={processScale}
      />
    );
    return renderProcess ? renderProcess(node) : node;
  }, [color, process, processScale, renderProcess, hideMember]);

  switch (status) {
    case MessageStatus.Sent:
    case MessageStatus.Summarized:
    case MessageStatus.Fetched:
    case MessageStatus.Decrypted:
      return processNode;
    default:
      return <MessageStatusBase processScale={processScale} status={status} {...props} />;
  }
}
